// src/pages/AlbumDetailPage.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, where, collection, query, getDocs, deleteDoc, updateDoc } from 'firebase/firestore';
import { useProfile } from '../context/ProfileContext';
import { useMusic } from '../context/MusicContext';
import { motion, AnimatePresence } from 'framer-motion';
import dropdownSidewaysIcon from '../assets/dropdownsideways_icon.png';
import EditAlbumModal from '../components/albums/EditAlbumModal';
import AddToAlbumModal from '../components/albums/AddToAlbumModal';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandleDots2Icon } from '@radix-ui/react-icons';
import toast from 'react-hot-toast';

// 🌟 UPGRADED: Added isDarkMode checking to structural rows
const SortableSongRow = ({ song, songIndex, isPlaying, onPlay, isDarkMode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: song.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <li 
      ref={setNodeRef} 
      style={style} 
      className={`flex items-center p-3 rounded-xl transition-colors ${
        isDarkMode 
          ? "hover:bg-zinc-900 text-white" 
          : "hover:bg-gray-100 text-slate-900"
      }`}
    >
      <div className="flex items-center flex-grow cursor-pointer min-w-0" onClick={onPlay}>
        <span className={`w-6 mr-4 text-center text-xs font-bold ${isDarkMode ? 'text-zinc-600' : 'text-gray-400'}`}>
          {songIndex + 1}
        </span>
        <img src={song.albumArtworkURL} alt={song.title} className="w-12 h-12 rounded-lg object-cover mr-4 shadow-md flex-shrink-0" />
        <div className="flex-grow min-w-0 pr-2">
          <p className={`font-bold text-sm tracking-tight truncate ${isPlaying ? 'text-brand-pink font-black italic' : ''}`}>
            {song.title}
          </p>
          <p className={`text-xs font-medium truncate ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>
            {song.artistName}
          </p>
        </div>
      </div>
      
      {/* Drag handle */}
      <div 
        {...attributes} 
        {...listeners} 
        className={`p-2 cursor-grab active:cursor-grabbing transition-colors touch-none flex-shrink-0 ${
          isDarkMode ? 'text-zinc-600 hover:text-zinc-400' : 'text-gray-400 hover:text-gray-600'
        }`}
      >
        <DragHandleDots2Icon />
      </div>
    </li>
  );
};

const AlbumDetailPage = () => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const db = getFirestore();
  const { activeProfile, isDarkMode } = useProfile(); // 🌟 IMPORT THE DYNAMIC PLATFORM THEME LAYER
  const { setQueueAndPlay, currentSong } = useMusic();
  
  const [album, setAlbum] = useState(null);
  const [songs, setSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [initialSongs, setInitialSongs] = useState([]);

  // Modal states
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddSongModalOpen, setIsAddSongModalOpen] = useState(false);
  const menuRef = useRef(null);

  const fetchAlbumAndSongs = useCallback(async () => {
    setIsLoading(true);
    try {
      const albumRef = doc(db, 'albums', albumId);
      const albumSnap = await getDoc(albumRef);

      if (!albumSnap.exists()) {
        toast.error("Album not found.");
        return navigate(-1);
      }
      
      const albumData = { id: albumSnap.id, ...albumSnap.data() };
      setAlbum(albumData);

      if (albumData.songIds && albumData.songIds.length > 0) {
        const songsRef = collection(db, 'songs');
        const q = query(songsRef, where('__name__', 'in', albumData.songIds));
        const songDocs = await getDocs(q);
        const fetchedSongs = songDocs.docs.map(d => ({ id: d.id, ...d.data() }));
        
        fetchedSongs.sort((a, b) => albumData.songIds.indexOf(a.id) - albumData.songIds.indexOf(b.id));
        setSongs(fetchedSongs);
        setInitialSongs(fetchedSongs); 
      } else {
        setSongs([]);
        setInitialSongs([]);
      }

    } catch (error) {
      toast.error("Error fetching album data.");
    } finally {
      setIsLoading(false);
    }
  }, [albumId, db, navigate]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSongs((items) => {
        const oldIndex = items.findIndex(s => s.id === active.id);
        const newIndex = items.findIndex(s => s.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveOrder = useCallback(async () => {
    const hasOrderChanged = JSON.stringify(initialSongs.map(s => s.id)) !== JSON.stringify(songs.map(s => s.id));
    if (!hasOrderChanged) return;

    const albumRef = doc(db, 'albums', albumId);
    try {
      const newSongIds = songs.map(s => s.id);
      await updateDoc(albumRef, { songIds: newSongIds });
      setInitialSongs(songs); 
      toast.success("Album order saved!");
    } catch (error) {
      toast.error("Could not save new order.");
    }
  }, [albumId, db, initialSongs, songs]);

  const saveCallback = useRef();
  useEffect(() => {
    saveCallback.current = handleSaveOrder;
  }, [handleSaveOrder]);

  useEffect(() => {
    return () => saveCallback.current();
  }, []);

  useEffect(() => {
    fetchAlbumAndSongs();
  }, [fetchAlbumAndSongs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDeleteAlbum = async () => {
    setIsMenuOpen(false);
    if (window.confirm(`Are you sure you want to delete the album "${album.name}"? This cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, 'albums', albumId));
        toast.success("Album deleted.");
        navigate(`/profile/${album.artistId}?tab=albums`);
      } catch (error) {
        toast.error("Failed to delete album.");
      }
    }
  };
  
  const isOwner = activeProfile?.id && album?.artistId === activeProfile.id;

  if (isLoading) {
    return <div className={`text-center p-10 text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-zinc-600 animate-pulse' : 'text-gray-400 animate-pulse'}`}>Loading Album...</div>;
  }

  if (!album) {
    return <div className={`text-center p-10 text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>Album not found.</div>;
  }

  return (
    <div className={`p-4 relative pb-36 min-h-screen transition-colors duration-500 ${isDarkMode ? 'bg-[#090909] text-white' : 'bg-white text-slate-900'}`}>
      
      {/* Dropdown Options Drawer Menu for owners */}
      {isOwner && (
        <div ref={menuRef} className="absolute top-4 right-4 z-20">
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)} 
            className={`p-2 rounded-full transition-colors focus:outline-none ${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-200'}`}
          >
            <img src={dropdownSidewaysIcon} alt="Options" className={`w-6 h-6 ${isDarkMode ? 'invert opacity-70' : ''}`} />
          </button>
          
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div 
                className={`absolute right-0 mt-2 w-48 border rounded-xl shadow-2xl py-1.5 z-30 ${
                  isDarkMode ? 'bg-zinc-950 border-zinc-800 text-zinc-300' : 'bg-white border-slate-100 text-slate-700'
                }`} 
                initial={{ opacity: 0, scale: 0.95, y: -10 }} 
                animate={{ opacity: 1, scale: 1, y: 0 }} 
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15 }}
              >
                <button onClick={() => { setIsEditModalOpen(true); setIsMenuOpen(false); }} className={`block w-full text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${isDarkMode ? 'hover:bg-zinc-900 hover:text-white' : 'hover:bg-gray-50 hover:text-slate-900'}`}>Edit Details</button>
                <button onClick={() => { setIsAddSongModalOpen(true); setIsMenuOpen(false); }} className={`block w-full text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${isDarkMode ? 'hover:bg-zinc-900 hover:text-white' : 'hover:bg-gray-50 hover:text-slate-900'}`}>Add Songs</button>
                <button onClick={handleDeleteAlbum} className={`block w-full text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${isDarkMode ? 'hover:bg-zinc-900 hover:text-red-400' : 'hover:bg-gray-50 hover:text-red-600'}`}>Delete Album</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Dynamic Album Metadata Header Box Component */}
      <div className="flex flex-col items-center text-center mb-8 pt-8">
        <img 
          src={album.artworkUrl} 
          alt={album.name} 
          className={`w-40 h-40 rounded-2xl object-cover shadow-2xl mb-4 border ${isDarkMode ? 'border-zinc-800/80 shadow-black/40' : 'border-slate-100 shadow-slate-200'}`} 
        />
        <h1 className="text-2xl md:text-3xl font-black italic uppercase tracking-tighter">{album.name}</h1>
        {album.description && (
          <p className={`text-xs font-semibold leading-relaxed max-w-prose mt-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
            {album.description}
          </p>
        )}
        <p className={`text-[10px] font-bold uppercase tracking-widest mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>
          {songs.length} tracks
        </p>
      </div>
      
      {/* Responsive List Stream Chassis */}
      <ul className="space-y-1 max-w-screen-md mx-auto">
        {isOwner ? (
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={songs.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {songs.map((song, index) => (
                <SortableSongRow
                  key={song.id}
                  song={song}
                  songIndex={index}
                  isPlaying={currentSong?.id === song.id}
                  onPlay={() => setQueueAndPlay(songs, index, album.name)}
                  isDarkMode={isDarkMode} // 🌟 Forward the theme flag downstream
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          songs.map((song, index) => (
            <li 
              key={song.id} 
              onClick={() => setQueueAndPlay(songs, index, album.name)} 
              className={`flex items-center p-3 rounded-xl cursor-pointer transition-colors ${
                isDarkMode 
                  ? "hover:bg-zinc-900 text-white" 
                  : "hover:bg-gray-100 text-slate-900"
              }`}
            >
              <span className={`w-6 mr-4 text-center text-xs font-bold ${isDarkMode ? 'text-zinc-600' : 'text-gray-400'}`}>
                {index + 1}
              </span>
              <img src={song.albumArtworkURL} alt={song.title} className="w-12 h-12 rounded-lg object-cover mr-4 shadow-md flex-shrink-0" />
              <div className="flex-grow min-w-0 pr-2">
                <p className={`font-bold text-sm tracking-tight truncate ${currentSong?.id === song.id ? 'text-brand-pink font-black italic' : ''}`}>
                  {song.title}
                </p>
                <p className={`text-xs font-medium truncate ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>
                  {song.artistName}
                </p>
              </div>
            </li>
          ))
        )}
      </ul>

      {/* Forward Theme variables down through configuration layers */}
      {isOwner && (
        <>
          <EditAlbumModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            album={album}
            onAlbumUpdated={fetchAlbumAndSongs}
            isDarkMode={isDarkMode} // Forward parameter
          />
          <AddToAlbumModal
            isOpen={isAddSongModalOpen}
            onClose={() => setIsAddSongModalOpen(false)}
            album={album}
            onSongAdded={fetchAlbumAndSongs}
            isDarkMode={isDarkMode} // Forward parameter
          />
        </>
      )}
    </div>
  );
};

export default AlbumDetailPage;